import { ArrowUpRight, ChevronRight } from 'lucide-react'
import { Button, ButtonGroup, Collapsible } from '@highlight/ui'
import { StagedActionMarkdown } from '@renderer/chat/integrations/staged-action'
import { ActionTagArea } from '@renderer/components/action-tags'
import { CopyIconButton } from '@renderer/overlay/components/copy-icon-button'
import type { NestedActionView } from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'
import { resolveContactAvatarSrc } from '@renderer/utils/contact-avatar'
import { twMerge } from 'tailwind-merge'

interface NestedViewProps {
  view: NestedActionView['view']
  /** Preview mode: gradient mask on content, stopPropagation on clicks, hide extra buttons */
  preview?: boolean
  className?: string
  /**
   * Reports the result-link buttons. The nested view holds no staged action, so
   * the caller that owns one supplies the attribution.
   */
  onButtonAction?: (target: 'copy link' | 'open link') => void
}

export function NestedView({ view, preview = false, className, onButtonAction }: NestedViewProps) {
  const hasTags = view.tags.length > 0 || (view.tagOrder?.length ?? 0) > 0
  const commenterName = view.contentMeta?.commenterName
  const commenterIcon = resolveContactAvatarSrc(view.contentMeta?.commenterIcon)
  const primaryButton = view.buttons[0]
  const extraButtons = view.buttons.slice(1)
  const hasBody =
    !!commenterName || !!commenterIcon || !!view.content || hasTags || (!preview && extraButtons.length > 0)

  return (
    <div onClick={preview ? (e) => e.stopPropagation() : undefined} className={twMerge(preview && 'mb-1.5', className)}>
      <Collapsible
        className={twMerge(
          'border-border bg-element group gap-0 rounded-[10px] border p-2 pl-2.5',
          !preview && 'mb-4',
          !preview && !hasBody && primaryButton && 'cursor-pointer',
        )}
        onClick={
          !preview && !hasBody && primaryButton
            ? () => {
                onButtonAction?.('open link')
                window.open(primaryButton.link, '_blank')
              }
            : undefined
        }
      >
        <div className="relative flex items-center gap-2">
          <Collapsible.Trigger
            className={twMerge(
              'flex min-w-0 items-center gap-2 rounded-md font-normal outline-none',
              "after:absolute after:-inset-2 after:cursor-pointer after:rounded-md after:transition-colors after:content-['']",
            )}
            showIcon={false}
          >
            {view.icon && <img src={view.icon} alt="" className="size-3.5 shrink-0 rounded" />}
            {view.title && (
              <span className="text-foreground-placeholder min-w-0 truncate text-left text-sm font-medium">
                {view.title}
              </span>
            )}
            {hasBody && (
              <ChevronRight className="text-foreground-placeholder size-3 shrink-0 transition-all ease-out group-data-[panel-open]:rotate-90" />
            )}
          </Collapsible.Trigger>
          {primaryButton && (
            <div className="relative ml-auto flex shrink-0 items-center gap-1">
              {!preview && (
                <CopyIconButton
                  size="sm"
                  value={primaryButton.link}
                  icon="link"
                  ariaLabel={`Copy link for ${primaryButton.label}`}
                  stopPropagation
                  onCopy={() => onButtonAction?.('copy link')}
                />
              )}
              <Button
                type="button"
                shape="square"
                size="xs"
                variant="ghost"
                aria-label={primaryButton.label}
                onClick={(e) => {
                  e.stopPropagation()
                  onButtonAction?.('open link')
                  window.open(primaryButton.link, '_blank')
                }}
                className="relative shrink-0"
              >
                <ArrowUpRight className="text-foreground-placeholder size-3.5 transition-transform duration-150 ease-out group-hover:-translate-y-px group-hover:translate-x-px" />
              </Button>
            </div>
          )}
        </div>

        {hasBody && (
          <Collapsible.Content innerClassName="mt-2 gap-2">
            {hasTags && <ActionTagArea tags={view.tags} tagOrder={view.tagOrder} />}

            {(commenterName || commenterIcon || view.content) && (
              <div className="flex items-start gap-2">
                {commenterIcon && <img src={commenterIcon} alt="" className="size-6 shrink-0 rounded-full" />}
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  {commenterName && <span className="text-foreground text-sm font-semibold">{commenterName}</span>}
                  {view.content && (
                    <div
                      className={
                        preview
                          ? 'relative max-h-[4.5rem] overflow-hidden whitespace-normal [-webkit-mask-image:linear-gradient(to_bottom,black_60%,transparent_100%)] [mask-image:linear-gradient(to_bottom,black_60%,transparent_100%)]'
                          : undefined
                      }
                    >
                      <StagedActionMarkdown
                        size="sm"
                        attachments={view.attachments}
                        className="text-foreground-secondary"
                      >
                        {view.content}
                      </StagedActionMarkdown>
                    </div>
                  )}
                </div>
              </div>
            )}

            {!preview && extraButtons.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                {extraButtons.map((button) => (
                  <ButtonGroup key={button.link} size="sm" variant="outline">
                    <CopyIconButton
                      value={button.link}
                      icon="link"
                      ariaLabel={`Copy link for ${button.label}`}
                      onCopy={() => onButtonAction?.('copy link')}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        onButtonAction?.('open link')
                        window.open(button.link, '_blank')
                      }}
                    >
                      {button.label}
                    </Button>
                  </ButtonGroup>
                ))}
              </div>
            )}
          </Collapsible.Content>
        )}
      </Collapsible>
    </div>
  )
}
